import { ApiError } from '@deskworks/api-client';
import type {
  ConnectionType,
  DeskDetail,
  DeskInstallPlan,
  DeskInstallResult,
  DeskStoreEntry,
  InstallConnectionChoice,
} from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, CheckCircle2, CircleAlert, Plug, Zap } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Field } from '@/components/field';
import { ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation, useDesks, useWorkspace } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

const CONNECTION_TYPE_LABEL: Partial<Record<ConnectionType, string>> = {
  mcp_server: t('MCP server'),
  git_repository: t('Git repository'),
  http_api: t('HTTP API'),
};

/** The address field a new connection of this type needs. */
const ADDRESS_FIELD: Partial<Record<ConnectionType, { key: string; label: string; placeholder: string }>> = {
  mcp_server: { key: 'url', label: t('Server URL'), placeholder: 'https://mcp.example.com/mcp' },
  git_repository: { key: 'url', label: t('Clone URL (HTTPS)'), placeholder: 'https://gitlab.example.com/group/app.git' },
  http_api: { key: 'baseUrl', label: t('Base URL'), placeholder: 'https://api.example.com' },
};

/** An MCP connection served by one of Deskworks's adapters (docs/features/mcp-adapters.md) needs the instance URL instead. */
function addressField(c: { type: ConnectionType; config: Record<string, unknown> }) {
  if (c.type === 'mcp_server' && c.config.adapter) return { key: 'baseUrl', label: t('Instance URL'), placeholder: 'https://gitlab.example.com' };
  return ADDRESS_FIELD[c.type];
}

type Choice = { mode: 'existing'; connectionId: string } | { mode: 'create'; address: string } | { mode: 'skip' };

function initialChoice(c: DeskInstallPlan['connections'][number]): Choice {
  if (c.suggested.mode === 'existing') return { mode: 'existing', connectionId: c.suggested.connectionId };
  const field = addressField(c);
  return { mode: 'create', address: field ? String(c.config[field.key] ?? '') : '' };
}

/** Install a Desk Store entry into a workspace: which desks and their names, connection per slot (existing, new, skip), triggers on or off. */
export function InstallDeskDialog({ workspaceId, entry, children }: { workspaceId: string; entry: DeskStoreEntry; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [names, setNames] = useState<Record<string, string>>({});
  const [included, setIncluded] = useState<Record<string, boolean>>({});
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [enableTriggers, setEnableTriggers] = useState(false);
  const [result, setResult] = useState<DeskInstallResult | null>(null);

  const plan = useQuery({
    queryKey: qk.deskStorePlan(workspaceId, entry.id),
    queryFn: () => api.deskStore.plan(workspaceId, entry.id),
    enabled: open,
    staleTime: 0,
  });

  // Start from the plan's suggestions each time the dialog opens.
  useEffect(() => {
    if (!open || !plan.data) return;
    const taken = new Set(plan.data.takenNames.map((n) => n.toLowerCase()));
    setNames(Object.fromEntries(plan.data.desks.map((d) => [d.key, taken.has(d.name.toLowerCase()) ? `${d.name} 2` : d.name])));
    setIncluded(Object.fromEntries(plan.data.desks.map((d) => [d.key, true])));
    setChoices(Object.fromEntries(plan.data.connections.map((c) => [c.key, initialChoice(c)])));
  }, [open, plan.data]);

  const p = plan.data;
  const several = (p?.desks.length ?? 0) > 1;
  const chosen = p?.desks.filter((d) => included[d.key] !== false) ?? [];
  const slots = p?.connections.filter((c) => c.desks.some((k) => included[k] !== false)) ?? [];
  const nameError = (key: string): string | undefined => {
    const name = (names[key] ?? '').trim().toLowerCase();
    if (!name || included[key] === false) return undefined;
    if (p?.takenNames.some((n) => n.toLowerCase() === name)) return t('This workspace already has a desk with this name.');
    if (chosen.some((d) => d.key !== key && (names[d.key] ?? '').trim().toLowerCase() === name)) return t('Two desks would have this name.');
    return undefined;
  };
  const ready = chosen.length > 0 && chosen.every((d) => (names[d.key] ?? '').trim() && !nameError(d.key));

  const install = useApiMutation(
    () => {
      const connections: Record<string, InstallConnectionChoice> = {};
      for (const c of slots) {
        const choice = choices[c.key];
        if (!choice) continue;
        const field = addressField(c);
        connections[c.key] =
          choice.mode === 'create'
            ? { mode: 'create', ...(field ? { config: { [field.key]: choice.address.trim() } } : {}) }
            : choice;
      }
      const desks = Object.fromEntries((p?.desks ?? []).map((d) => [d.key, { name: (names[d.key] ?? '').trim(), install: included[d.key] !== false }]));
      return api.deskStore.install(workspaceId, entry.id, { desks, connections, enableTriggers });
    },
    {
      errorTitle: several ? t('The desks were not installed') : t('The desk was not installed'),
      invalidate: [qk.desks(workspaceId), qk.overview(workspaceId), qk.connections(workspaceId), qk.agents(workspaceId), qk.skills(workspaceId), qk.deskStore(workspaceId)],
      onSuccess: setResult,
    },
  );

  const onOpenChange = (o: boolean) => {
    if (o) {
      setResult(null);
      setEnableTriggers(false);
      install.reset();
    }
    setOpen(o);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) install.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        {result ? (
          <InstallDone workspaceId={workspaceId} entry={entry} result={result} onClose={() => setOpen(false)} />
        ) : (
          <form onSubmit={onSubmit} className="grid gap-5">
            <DialogHeader>
              <DialogTitle>{t('Install “{name}”', { name: entry.name })}</DialogTitle>
              <DialogDescription>
                {entry.contents.desks.length > 1
                  ? t(
                      'Creates the desks with agents, goals, playbook, skills, triggers and tables. Connections come without keys or sign-ins: use one you already have, or create it and sign in afterwards.',
                    )
                  : t(
                      'Creates the desk with agents, goals, playbook, skills, triggers and tables. Connections come without keys or sign-ins: use one you already have, or create it and sign in afterwards.',
                    )}
              </DialogDescription>
            </DialogHeader>
            {plan.isPending ? (
              <LoadingRows rows={3} />
            ) : plan.isError ? (
              <ErrorState error={plan.error} onRetry={() => void plan.refetch()} />
            ) : (
              <>
                {several ? (
                  <div className="grid gap-3">
                    <div>
                      <h3 className="text-sm font-medium">{t('Desks')}</h3>
                      <p className="text-xs text-muted-foreground">{t('Installed together; leave out the ones this workspace doesn’t need.')}</p>
                    </div>
                    {plan.data.desks.map((d) => (
                      <DeskSlot
                        key={d.key}
                        desk={d}
                        name={names[d.key] ?? ''}
                        included={included[d.key] !== false}
                        error={nameError(d.key)}
                        onName={(name) => setNames((prev) => ({ ...prev, [d.key]: name }))}
                        onIncluded={(on) => setIncluded((prev) => ({ ...prev, [d.key]: on }))}
                      />
                    ))}
                  </div>
                ) : (
                  plan.data.desks.map((d) => (
                    <Field key={d.key} label={t('Desk name')} error={nameError(d.key)}>
                      <Input value={names[d.key] ?? ''} onChange={(e) => setNames((prev) => ({ ...prev, [d.key]: e.target.value }))} required />
                    </Field>
                  ))
                )}
                <InstallOptions
                  plan={plan.data}
                  slots={slots}
                  deskNames={names}
                  choices={choices}
                  onChoice={(key, choice) => setChoices((prev) => ({ ...prev, [key]: choice }))}
                  enableTriggers={enableTriggers}
                  onEnableTriggers={setEnableTriggers}
                />
              </>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {t('Cancel')}
              </Button>
              <Button type="submit" loading={install.isPending} disabled={!p || !ready}>
                {several ? (chosen.length === 1 ? t('Install 1 desk') : t('Install {n} desks', { n: chosen.length })) : t('Install desk')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DeskSlot({
  desk,
  name,
  included,
  error,
  onName,
  onIncluded,
}: {
  desk: DeskInstallPlan['desks'][number];
  name: string;
  included: boolean;
  error?: string;
  onName: (name: string) => void;
  onIncluded: (on: boolean) => void;
}) {
  const renamed = included && name.trim() !== '' && name.trim() !== desk.name;
  return (
    <div className={cn('grid gap-2 rounded-md border p-3', !included && 'bg-muted/40')}>
      <label className="flex items-center gap-2">
        <Checkbox checked={included} onChange={(e) => onIncluded(e.target.checked)} />
        <span className="text-sm font-medium">{desk.name}</span>
        {desk.lead && <span className="text-xs text-muted-foreground">{t('Lead: {name}', { name: desk.lead })}</span>}
      </label>
      {included && (
        <Field label={t('Name in this workspace')} error={error}>
          <Input value={name} onChange={(e) => onName(e.target.value)} required />
        </Field>
      )}
      {renamed && desk.mentionedBy.length > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {desk.mentionedBy.length === 1
            ? t('{desks} refers to this desk as “{name}”. Keep the name, or update its playbook after installing.', {
                desks: desk.mentionedBy.map((n) => `“${n}”`).join(', '),
                name: desk.name,
              })
            : t('{desks} refer to this desk as “{name}”. Keep the name, or update their playbook after installing.', {
                desks: desk.mentionedBy.map((n) => `“${n}”`).join(', '),
                name: desk.name,
              })}
        </p>
      )}
    </div>
  );
}

/** Connections, notes and the trigger switch, for one desk or several. */
function InstallOptions({
  plan,
  slots,
  deskNames,
  choices,
  onChoice,
  enableTriggers,
  onEnableTriggers,
}: {
  plan: DeskInstallPlan;
  slots: DeskInstallPlan['connections'];
  deskNames: Record<string, string>;
  choices: Record<string, Choice>;
  onChoice: (key: string, choice: Choice) => void;
  enableTriggers: boolean;
  onEnableTriggers: (on: boolean) => void;
}) {
  const several = plan.desks.length > 1;
  return (
    <>
      {slots.length > 0 && (
        <div className="grid gap-3">
          <div>
            <h3 className="text-sm font-medium">{t('Connections')}</h3>
            <p className="text-xs text-muted-foreground">
              {several
                ? t('Which system each part of the desks uses in this workspace. A connection several desks use is set up once.')
                : t('Which system each part of the desk uses in this workspace.')}
            </p>
          </div>
          {slots.map((c) => (
            <ConnectionSlot
              key={c.key}
              slot={c}
              usedBy={several ? c.desks.map((k) => deskNames[k] || k) : undefined}
              choice={choices[c.key] ?? initialChoice(c)}
              onChange={(choice) => onChoice(c.key, choice)}
            />
          ))}
        </div>
      )}

      <PlanNotes plan={plan} />

      <label className="flex items-start gap-3 rounded-md border p-3">
        <Switch checked={enableTriggers} onCheckedChange={onEnableTriggers} className="mt-0.5" />
        <span className="grid gap-0.5">
          <span className="text-sm font-medium">{t('Switch on triggers right away')}</span>
          <span className="text-xs text-muted-foreground">
            {several
              ? t('Off: the desks only work when you start them, until you switch their triggers on – best while connections still need a sign-in.')
              : t('Off: the desk only works when you start it, until you switch its triggers on – best while connections still need a sign-in.')}
          </span>
        </span>
      </label>
    </>
  );
}

function ConnectionSlot({
  slot,
  usedBy,
  choice,
  onChange,
}: {
  slot: DeskInstallPlan['connections'][number];
  usedBy?: string[];
  choice: Choice;
  onChange: (c: Choice) => void;
}) {
  const field = addressField(slot);
  const value = choice.mode === 'existing' ? `existing:${choice.connectionId}` : choice.mode;
  const onSelect = (v: string) => {
    if (v.startsWith('existing:')) onChange({ mode: 'existing', connectionId: v.slice('existing:'.length) });
    else if (v === 'create') onChange({ mode: 'create', address: field ? String(slot.config[field.key] ?? '') : '' });
    else onChange({ mode: 'skip' });
  };
  return (
    <div className="grid gap-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Plug className="size-4 text-muted-foreground" />
        <span className="text-sm font-medium">{slot.name}</span>
        <Badge tone="muted">{CONNECTION_TYPE_LABEL[slot.type] ?? slot.type}</Badge>
        <Badge tone={slot.permissions.includes('write') ? 'amber' : 'neutral'}>{slot.permissions.includes('write') ? t('read + write') : t('read only')}</Badge>
      </div>
      {slot.description && <p className="text-xs text-muted-foreground">{slot.description}</p>}
      {usedBy && <p className="text-xs text-muted-foreground">{t('Used by {names}', { names: usedBy.join(', ') })}</p>}
      <Select value={value} onValueChange={onSelect}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {slot.candidates.map((x) => (
            <SelectItem key={x.id} value={`existing:${x.id}`}>
              {t('Use “{name}”', { name: x.name })}
            </SelectItem>
          ))}
          <SelectItem value="create">{t('Create a new connection')}</SelectItem>
          <SelectItem value="skip">{t('Skip for now')}</SelectItem>
        </SelectContent>
      </Select>
      {choice.mode === 'create' && field && (
        <Field label={field.label} hint={slot.setup || undefined}>
          <Input
            value={choice.address}
            onChange={(e) => onChange({ mode: 'create', address: e.target.value })}
            placeholder={field.placeholder}
            required
          />
        </Field>
      )}
      {choice.mode === 'create' && !field && slot.setup && <p className="text-xs text-muted-foreground">{slot.setup}</p>}
    </div>
  );
}

function PlanNotes({ plan }: { plan: DeskInstallPlan }) {
  const several = plan.desks.length > 1;
  const notes: string[] = [
    ...plan.renamedAgents.map((r) => t('Agent “{from}” is created as “{to}”: the workspace already has an agent with that name.', { from: r.from, to: r.to })),
    ...plan.existingSkills.map((s) =>
      several
        ? t('Skill “{name}” exists in this workspace; the desks use yours.', { name: s })
        : t('Skill “{name}” exists in this workspace; the desk uses yours.', { name: s }),
    ),
    ...plan.missingSkills.map((s) => t('Skill “{name}” is missing; create it under Skills later.', { name: s })),
    ...plan.existingTables.map((table) => t('Table {name} already exists in the workspace database and is kept as it is.', { name: table })),
  ];
  if (!notes.length) return null;
  return (
    <ul className="grid gap-1 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
      {notes.map((n) => (
        <li key={n}>{n}</li>
      ))}
    </ul>
  );
}

function InstallDone({ workspaceId, entry, result, onClose }: { workspaceId: string; entry: DeskStoreEntry; result: DeskInstallResult; onClose: () => void }) {
  const navigate = useNavigate();
  const [only] = result.desks.length === 1 ? result.desks : [];
  const openDesks = () => {
    onClose();
    if (only) void navigate({ to: '/w/$workspaceId/desks/$deskId', params: { workspaceId, deskId: only.deskId } });
    else void navigate({ to: '/w/$workspaceId/desks', params: { workspaceId } });
  };
  return (
    <div className="grid gap-5">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle2 className="size-5 text-emerald-600" /> {t('“{name}” is installed', { name: entry.name })}
        </DialogTitle>
        <DialogDescription>{result.todo.length ? t('A few things are left for you:') : t('Everything is ready.')}</DialogDescription>
      </DialogHeader>
      {!only && (
        <div className="flex flex-wrap gap-2">
          {result.desks.map((d) => (
            <Link
              key={d.deskId}
              to="/w/$workspaceId/desks/$deskId"
              params={{ workspaceId, deskId: d.deskId }}
              onClick={onClose}
              className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-sm font-medium hover:bg-muted"
            >
              {d.name} <ArrowRight className="size-3.5" />
            </Link>
          ))}
        </div>
      )}
      {result.todo.length > 0 && (
        <ul className="grid gap-2">
          {result.todo.map((todo, i) => (
            <li key={i} className="flex gap-3 rounded-md border p-3">
              {todo.kind === 'trigger' ? <Zap className="mt-0.5 size-4 shrink-0 text-muted-foreground" /> : todo.kind === 'connection' ? <Plug className="mt-0.5 size-4 shrink-0 text-muted-foreground" /> : <CircleAlert className="mt-0.5 size-4 shrink-0 text-muted-foreground" />}
              <div className="grid min-w-0 gap-0.5">
                <span className="text-sm font-medium">{todo.title}</span>
                <span className="text-xs text-muted-foreground">{todo.detail}</span>
                {todo.connectionId && (
                  <Link
                    to="/w/$workspaceId/connections/$connectionId"
                    params={{ workspaceId, connectionId: todo.connectionId }}
                    onClick={onClose}
                    className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    {t('Open connection')} <ArrowRight className="size-3" />
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <DialogFooter>
        <Button onClick={openDesks}>
          {only ? t('Open desk') : t('Open desks')} <ArrowRight />
        </Button>
      </DialogFooter>
    </div>
  );
}

/** Saves a desk into the account's Desk Store, without any data. */
export function SaveToDeskStoreDialog({
  desk,
  children,
  open: controlledOpen,
  onOpenChange,
}: {
  desk: DeskDetail;
  children?: ReactNode; open?: boolean; onOpenChange?: (open: boolean) => void;
}) {
  const [innerOpen, setInnerOpen] = useState(false);
  const open = controlledOpen ?? innerOpen;
  const setOpen = onOpenChange ?? setInnerOpen;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [exists, setExists] = useState<string | null>(null);

  const template = useQuery({ queryKey: qk.deskTemplate(desk.id), queryFn: () => api.desks.template(desk.id), enabled: open, staleTime: 0 });

  const save = useApiMutation((replace: boolean) => api.desks.saveToStore(desk.id, { name: name.trim(), description: description.trim(), category: category.trim(), replace }), {
    success: (entry) => t('“{name}” is in the Desk Store', { name: entry.name }),
    errorTitle: t('Not saved'),
    invalidate: [qk.deskStore(desk.workspaceId)],
    onSuccess: () => setOpen(false),
  });

  // Fresh on every opening, from its own trigger or from outside.
  useEffect(() => {
    if (!open) return;
    setName(desk.name);
    setDescription(desk.assignment);
    setCategory('');
    setExists(null);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = (replace: boolean) =>
    save.mutate(replace, {
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) setExists(err.message);
      },
    });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) submit(false);
  };

  const tpl = template.data;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('Save to Desk Store')}</DialogTitle>
            <DialogDescription>
              {t('Every workspace of your account can then install this desk. Only the setup is saved – no Work Items, chats, table rows, keys or sign-ins.')}
            </DialogDescription>
          </DialogHeader>
          <Field label={t('Name')}>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setExists(null);
              }}
              required
            />
          </Field>
          <Field label={t('Description')} hint={t('What the desk does, for whoever installs it.')} optional>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </Field>
          <Field label={t('Category')} optional>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder={t('e.g. Customer service')} />
          </Field>
          <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
            {template.isPending ? (
              t('Reading the desk…')
            ) : template.isError ? (
              <span className="text-destructive">{(template.error as Error).message}</span>
            ) : tpl ? (
              <ul className="grid gap-1">
                <li>
                  <span className="font-medium text-foreground">{t('Agents:')}</span>{' '}
                  {tpl.agents.map((a) => (a.role === 'lead' ? t('{name} (lead)', { name: a.name }) : a.name)).join(', ')}
                </li>
                <li>
                  <span className="font-medium text-foreground">{t('Connections (without keys):')}</span>{' '}
                  {tpl.connections.length ? tpl.connections.map((c) => c.name).join(', ') : t('none')}
                </li>
                <li>
                  <span className="font-medium text-foreground">{t('Skills:')}</span> {[...new Set(tpl.agents.flatMap((a) => a.skills))].join(', ') || t('none')}
                </li>
                <li>
                  <span className="font-medium text-foreground">{t('Goals, playbook, triggers:')}</span>{' '}
                  {tpl.desk.playbook.trim()
                    ? t('{goals} goals, playbook, {triggers} triggers', { goals: tpl.desk.goals.length, triggers: tpl.triggers.length })
                    : t('{goals} goals, no playbook, {triggers} triggers', { goals: tpl.desk.goals.length, triggers: tpl.triggers.length })}
                </li>
                <li>
                  <span className="font-medium text-foreground">{t('Tables (structure only):')}</span> {tpl.tables.map((x) => x.name).join(', ') || t('none')}
                </li>
              </ul>
            ) : null}
          </div>
          {exists && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
              <CircleAlert className="mt-0.5 size-4 shrink-0" />
              <span>{exists}</span>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            {exists ? (
              <Button type="button" loading={save.isPending} onClick={() => submit(true)}>
                {t('Replace it')}
              </Button>
            ) : (
              <Button type="submit" loading={save.isPending} disabled={!name.trim() || template.isError}>
                {t('Save')}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Saves desks of the workspace as one Desk Store entry, so another workspace is set up with all of them at once. */
export function SaveDesksToStoreDialog({ workspaceId, children }: { workspaceId: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [exists, setExists] = useState<string | null>(null);
  const desks = useDesks(workspaceId);
  const workspace = useWorkspace(workspaceId);

  const save = useApiMutation(
    (replace: boolean) =>
      api.deskStore.saveDesks(workspaceId, {
        name: name.trim(),
        description: description.trim(),
        category: category.trim(),
        deskIds: (desks.data ?? []).filter((d) => selected.has(d.id)).map((d) => d.id),
        replace,
      }),
    {
      success: (entry) =>
        entry.contents.desks.length === 1
          ? t('“{name}” is in the Desk Store with 1 desk', { name: entry.name })
          : t('“{name}” is in the Desk Store with {n} desks', { name: entry.name, n: entry.contents.desks.length }),
      errorTitle: t('Not saved'),
      invalidate: [qk.deskStore(workspaceId)],
      onSuccess: () => setOpen(false),
    },
  );

  // Fresh on every opening: every desk, named after the workspace.
  useEffect(() => {
    if (!open) return;
    setSelected(new Set((desks.data ?? []).filter((d) => d.status !== 'archived').map((d) => d.id)));
    setName(workspace.data?.name ?? '');
    setDescription('');
    setCategory('Workspace');
    setExists(null);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, desks.data, workspace.data]);

  const submit = (replace: boolean) =>
    save.mutate(replace, {
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) setExists(err.message);
      },
    });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && selected.size) submit(false);
  };

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const list = (desks.data ?? []).filter((d) => d.status !== 'archived');
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('Save desks to the Desk Store')}</DialogTitle>
            <DialogDescription>
              {t(
                'One entry with all the desks you choose, so another workspace is set up with them in one go. Agents and connections the desks share stay shared. Only the setup is saved – no Work Items, chats, table rows, keys or sign-ins.',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <span className="text-sm font-medium">{t('Desks')}</span>
            {desks.isPending ? (
              <LoadingRows rows={2} />
            ) : (
              <div className="grid gap-1 rounded-md border p-2">
                {list.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted/50">
                    <Checkbox checked={selected.has(d.id)} onChange={(e) => toggle(d.id, e.target.checked)} />
                    <span className="font-medium">{d.name}</span>
                    {d.leadAgent && <span className="text-xs text-muted-foreground">{t('Lead: {name}', { name: d.leadAgent.name })}</span>}
                  </label>
                ))}
              </div>
            )}
          </div>
          <Field label={t('Name')}>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setExists(null);
              }}
              required
            />
          </Field>
          <Field label={t('Description')} hint={t('What these desks do together, for whoever installs them.')} optional>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </Field>
          <Field label={t('Category')} optional>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} />
          </Field>
          {exists && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
              <CircleAlert className="mt-0.5 size-4 shrink-0" />
              <span>{exists}</span>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            {exists ? (
              <Button type="button" loading={save.isPending} onClick={() => submit(true)}>
                {t('Replace it')}
              </Button>
            ) : (
              <Button type="submit" loading={save.isPending} disabled={!name.trim() || !selected.size}>
                {selected.size === 1 ? t('Save 1 desk') : t('Save {n} desks', { n: selected.size })}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
